/**
 * Duplicate-expense detection (pure). A new expense is compared with existing ones on four
 * signals — merchant, amount, purchase date, payment method — each scored with a tolerance, then
 * combined into a weighted score. Duplicates are only ever *flagged*; the user decides.
 *
 * Gates (a pair is never a duplicate if):
 *   - the amounts differ by more than 2 % (and more than 1 cent)
 *   - the purchase dates are more than 3 days apart
 *   - both merchants are known and clearly different (similarity < 0.5)
 *   - neither has a merchant and their categories differ (no identity signal agrees)
 *   - the existing expense is voided or a draft, or is the same record
 *
 * Signal scores (0–1) and weights:
 *   amount  35 %  1.0 equal to the cent · 0.5 within 2 %
 *   date    20 %  1.0 same day · 0.7 ±1 day (receipt vs bank date) · 0 at ±2–3 days (still compared)
 *   merchant 25 % similarity of normalized names (1.0 same key, 0.8 contained, …);
 *                 both unknown → 0.6 when the category matches; one unknown → 0.5
 *   method  20 %  1.0 same · 0.0 different · 0.5 unknown (e.g. rows imported from Excel)
 *
 * Levels: score ≥ 0.85 "likely", ≥ 0.65 "possible", otherwise not reported.
 */
import type { Expense, IsoDate } from "./model";
import { merchantSimilarity } from "./merchants";
import { Decimal } from "./money";

export const DUPLICATE_WINDOW_DAYS = 3;
export const LIKELY_THRESHOLD = 0.85;
export const POSSIBLE_THRESHOLD = 0.65;

const WEIGHTS = { amount: 0.35, date: 0.2, merchant: 0.25, method: 0.2 } as const;

export type DuplicateReason =
  | "same_amount"
  | "similar_amount"
  | "same_date"
  | "near_date"
  | "same_merchant"
  | "similar_merchant"
  | "merchant_unknown"
  | "same_category"
  | "same_payment_method"
  | "different_payment_method"
  | "payment_method_unknown";

export type DuplicateLevel = "likely" | "possible";

export interface DuplicateMatch {
  expenseId: string;
  score: number;
  level: DuplicateLevel;
  reasons: DuplicateReason[];
}

/** What is compared: a new or edited expense before it is saved. */
export interface DuplicateProbe {
  id?: string;
  purchaseDate: IsoDate;
  amountEur: string;
  paymentMethodId: string | null;
  merchant: string | null;
  categoryId: string;
}

function dayNumber(iso: IsoDate): number {
  return Math.round(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))) / 86_400_000);
}

export function daysApart(a: IsoDate, b: IsoDate): number {
  return Math.abs(dayNumber(a) - dayNumber(b));
}

/** ISO dates bounding the comparison window around a purchase date. */
export function duplicateWindow(purchaseDate: IsoDate): { from: IsoDate; to: IsoDate } {
  const toIso = (n: number) => new Date(n * 86_400_000).toISOString().slice(0, 10);
  const d = dayNumber(purchaseDate);
  return { from: toIso(d - DUPLICATE_WINDOW_DAYS), to: toIso(d + DUPLICATE_WINDOW_DAYS) };
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Scores one pair; null when a gate rules it out. */
export function scoreDuplicate(probe: DuplicateProbe, existing: Expense): DuplicateMatch | null {
  if (existing.id === probe.id || existing.status !== "confirmed") return null;

  const reasons: DuplicateReason[] = [];

  const a = new Decimal(probe.amountEur);
  const b = new Decimal(existing.amountEur);
  const diff = a.minus(b).abs();
  let amount: number;
  if (diff.lte("0.01")) {
    amount = 1;
    reasons.push("same_amount");
  } else if (diff.lte(Decimal.max(a, b).times("0.02"))) {
    amount = 0.5;
    reasons.push("similar_amount");
  } else {
    return null;
  }

  const days = daysApart(probe.purchaseDate, existing.purchaseDate);
  if (days > DUPLICATE_WINDOW_DAYS) return null;
  const date = days === 0 ? 1 : days === 1 ? 0.7 : 0;
  reasons.push(days === 0 ? "same_date" : "near_date");

  let merchant: number;
  const similarity = merchantSimilarity(probe.merchant, existing.merchant);
  if (similarity !== null) {
    if (similarity < 0.5) return null;
    merchant = similarity;
    reasons.push(similarity === 1 ? "same_merchant" : "similar_merchant");
  } else if (!probe.merchant?.trim() && !existing.merchant?.trim()) {
    if (probe.categoryId !== existing.categoryId) return null;
    merchant = 0.6;
    reasons.push("merchant_unknown", "same_category");
  } else {
    merchant = 0.5;
    reasons.push("merchant_unknown");
  }

  let method: number;
  if (probe.paymentMethodId === null || existing.paymentMethodId === null) {
    method = 0.5;
    reasons.push("payment_method_unknown");
  } else if (probe.paymentMethodId === existing.paymentMethodId) {
    method = 1;
    reasons.push("same_payment_method");
  } else {
    method = 0;
    reasons.push("different_payment_method");
  }

  const score = round3(amount * WEIGHTS.amount + date * WEIGHTS.date + merchant * WEIGHTS.merchant + method * WEIGHTS.method);
  if (score < POSSIBLE_THRESHOLD) return null;
  return { expenseId: existing.id, score, level: score >= LIKELY_THRESHOLD ? "likely" : "possible", reasons };
}

/** All reportable matches, best first. `candidates` may contain anything; gates filter them. */
export function findDuplicates(probe: DuplicateProbe, candidates: readonly Expense[]): DuplicateMatch[] {
  const matches: DuplicateMatch[] = [];
  for (const existing of candidates) {
    const match = scoreDuplicate(probe, existing);
    if (match) matches.push(match);
  }
  return matches.sort((x, y) => y.score - x.score || x.expenseId.localeCompare(y.expenseId));
}
